import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api";
import type { ContentDetailDTO } from "../../shared/dto";
import { uploadsUrl } from "../lib/uploader";
import { fmtDate } from "../lib/format";
import { RichTextRender } from "../components/RichTextRender";
import { Loading, Banner } from "../components/ui";

const CONTENT_TYPE_LABELS: Record<string, string> = {
  article: "記事",
  video: "動画",
  pdf: "PDF",
  external_link: "外部リンク",
};
const CONTENT_TYPE_ICON: Record<string, string> = {
  article: "📄",
  video: "🎥",
  pdf: "📑",
  external_link: "🔗",
};

export function ContentDetailPage() {
  const { contentId } = useParams<{ contentId: string }>();
  const [data, setData] = useState<ContentDetailDTO | null>(null);

  useEffect(() => {
    if (!contentId) return;
    api.get<ContentDetailDTO>(`/contents/${contentId}`).then(setData);
  }, [contentId]);

  if (!data) return <Loading />;

  const firstAsset = data.assets?.[0];
  const isVideo = data.contentType === "video";
  const isPdf = data.contentType === "pdf";
  const isExternal = data.contentType === "external_link";
  const typeIcon = CONTENT_TYPE_ICON[data.contentType] ?? "📄";
  const typeLabel = CONTENT_TYPE_LABELS[data.contentType] ?? data.contentType;
  const thumb = uploadsUrl(data.thumbnailKey);

  return (
    <article className="mx-auto max-w-3xl space-y-5">
      <Link
        to="/contents"
        className="tap-target text-sm text-tora-ink/60 hover:text-tora"
      >
        ← コンテンツ一覧へ
      </Link>

      <header className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-md bg-tora/5 px-2 py-1 text-xs font-semibold text-tora-ink">
            <span aria-hidden="true">{typeIcon}</span>
            <span>{typeLabel}</span>
          </span>
          {data.publishedAt && (
            <span className="text-xs text-tora-ink/60">
              {fmtDate(data.publishedAt)}
            </span>
          )}
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-tora leading-tight">
          {data.title}
        </h2>
        {data.summary && (
          <p className="text-base md:text-lg text-tora-ink/70 leading-relaxed">
            {data.summary}
          </p>
        )}
      </header>

      {thumb && (
        <img
          src={thumb}
          alt=""
          className="w-full h-auto max-h-[70vh] object-contain rounded-xl border border-black/10 bg-black/5 shadow-sm"
        />
      )}

      {isVideo && firstAsset?.embedUrl && (
        <div className="aspect-video rounded-xl overflow-hidden border border-black/10 shadow-sm">
          <iframe
            src={firstAsset.embedUrl}
            className="w-full h-full"
            allow="autoplay; fullscreen; picture-in-picture"
          />
        </div>
      )}

      {isPdf &&
        (firstAsset?.fileKey ? (
          <a
            href={uploadsUrl(firstAsset.fileKey)!}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline btn-lg w-full md:w-auto"
          >
            PDF を開く 📑
          </a>
        ) : (
          <Banner tone="info">
            PDF がまだ登録されていません。管理者による登録をお待ちください。
          </Banner>
        ))}

      {isExternal && firstAsset?.externalUrl && (
        <a
          href={firstAsset.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-outline btn-lg w-full md:w-auto"
        >
          外部サイトで開く 🔗
        </a>
      )}

      <RichTextRender html={data.bodyMarkdown} />
    </article>
  );
}
